import type { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import SendIcon from '@mui/icons-material/Send';
import { Box, IconButton, TextField, useTheme } from '@mui/material';
import * as React from 'react';

interface Props {
  draftMessage: string;
  updateDraftMessage: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  sendMessage: () => void;
  isAssigneeOrHasSentMessage: boolean;
}

export const DrawerMessageInput = ({
  draftMessage,
  updateDraftMessage,
  sendMessage,
  isAssigneeOrHasSentMessage,
}: Props) => {
  const { tokens } = useTheme<LensTheme>();
  const color = tokens.semantic.color;

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey && draftMessage) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <Box
      sx={{
        border: `1px solid ${color.ui.divider.default.value}`,
        borderRadius: '12px',
        backgroundColor: color.surface.default.value,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        mx: '16px',
        mb: '16px',
      }}
    >
      <TextField
        multiline
        fullWidth
        variant="standard"
        placeholder={
          isAssigneeOrHasSentMessage
            ? 'Enter a message'
            : 'If you choose to message the reporter, we will inform the reporter that you entered the chat'
        }
        value={draftMessage || ''}
        onChange={
          updateDraftMessage as unknown as React.ChangeEventHandler<
            HTMLInputElement | HTMLTextAreaElement
          >
        }
        onKeyDown={
          handleKeyDown as unknown as React.KeyboardEventHandler<HTMLDivElement>
        }
        inputProps={{ 'data-testid': 'case-message-box-input' }}
        sx={{
          px: '8px',
          pt: '12px',
          '& .MuiInputBase-root': {
            fontSize: '16px',
            fontWeight: 400,
            lineHeight: '24px',
            letterSpacing: '0.2px',
            color: color.type.muted.value,
            '&:before': { display: 'none' },
            '&:after': { display: 'none' },
          },
        }}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-end',
          px: '8px',
          pb: '8px',
        }}
      >
        <IconButton
          onClick={sendMessage}
          disabled={!draftMessage}
          data-testid="case-message-send"
          size="medium"
          sx={{
            color: draftMessage
              ? color.type.default.value
              : color.type.muted.value,
          }}
        >
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};
